import * as deviceDetect from "deviceDetect";
import * as languageMap from "shared/languageMap";
import type ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import SyncAudioController from "SyncAudioController";
import type { SyncAudioControlsProps } from "SyncAudioControls";
import type { ApplyTranscriptMode } from "views/ApplyTranscriptDialog";

/**
 * The position an explicit route asks the video to start at, or undefined
 * when the route carries none.
 *
 * Zero is treated as "no explicit position": the route handler collapses
 * `/library/<id>/scroll` and `/scroll/0` into `options.scroll = 0` (see
 * app.ts), and a plain `/library/<id>` open of a video book is redirected to
 * `/scroll/0` by `enterScrollMode`, so an explicit zero isn't expressible -
 * falling back to saved progress is what keeps "reload resumes where I was"
 * working. Going to the start from within the reader seeks explicitly and
 * doesn't rely on this.
 *
 * `from`/`to` are included because a `/from/<n>` deep link into a video book
 * seeds the player here, before `enterScrollMode` redirects it to `/scroll/<n>`.
 */
const routeStartPosition = (options: any): number | undefined => {
  const position = Number(options?.scroll ?? options?.from ?? options?.to) || 0;
  return position > 0 ? position : undefined;
};

export const _functionsForUnitTests = { routeStartPosition };

interface ReaderSyncAudioOptions {
  appRouter: any;
  getMediaPlayerElement: () => HTMLElement | undefined;
  isOwner: () => boolean;
  markAsCompleted: () => void;
  onReadingPosition: (position: number) => void;
  options: any;
  getReaderTextStore: () => ReaderTextStore | undefined;
  readerViewStore: ReaderViewStore;
  setControlsState: (controlsState: SyncAudioControlsProps | undefined) => void;
  setMediaPanelHeight: (height: number, callback?: () => void) => void;
  setPageTextViewportNoFooter: (noFooter: boolean) => void;
  updateFooterState: (footerState: {
    hidden?: boolean;
    marginButtonsHidden?: boolean;
  }) => void;
  updateLayout: () => void;
  updateUserBookPosition: (position: number) => void;
}

export class ReaderSyncAudio {
  private controlsState?: SyncAudioControlsProps;
  private controller?: SyncAudioController;
  private lastPositionSession: number | undefined;
  private showControlsKey = 0;

  constructor(private deps: ReaderSyncAudioOptions) {}

  initialize() {
    const { options, getReaderTextStore, readerViewStore } = this.deps;
    const startPosition = this.getStartPosition();
    if (!getReaderTextStore()) {
      return;
    }

    this.controller = new SyncAudioController({
      el: "body",
      playerElement: this.deps.getMediaPlayerElement(),
      model: stores.bookModel,
      getReaderTextStore,
      readerViewStore,
      appRouter: this.deps.appRouter,
      scroll: "scroll" in options,
      gotoPosition: this.setSyncAudioPosition,
      startPosition,
      setMediaPanelHeight: this.setMediaPanelHeight,
      showFooter: this.showFooter,
      hideFooter: this.hideFooter,
      isOwner: this.deps.isOwner,
      enterScrollMode: this.enterScrollMode,
      showControls: this.showControls,
      textDirection:
        languageMap.map[stores.bookModel.book.value.language].direction ||
        "LTR",
      markAsCompleted: this.deps.markAsCompleted,
    });

    if (this.controller.hasMediaPlayer()) {
      this.controlsState = {
        initiallyVisible: !deviceDetect.is("iOS"),
        owner: this.deps.isOwner(),
        onClickModeChange: this.controller.setClickMode,
        showControlsKey: this.showControlsKey,
      };
      this.deps.setControlsState(this.controlsState);
    }
  }

  status() {
    return this.controller?.status();
  }

  updateOptions(options: any) {
    this.deps = { ...this.deps, options };
    this.controller?.updateOptions({
      scroll: "scroll" in options,
    });
    // Each page load replaces the reader text store; re-register the
    // sync/goto click-mode handlers on the current one.
    this.controller?.bindTextStore();
  }

  /**
   * Called for every navigation that reaches the mounted reader, including a
   * repeat of the position it is already on (which loads no new page), so a
   * scroll-mode target always seeks the video - e.g. "go to start" from
   * /scroll/0 after playback has moved on, or repeating a Find in text
   * target.
   *
   * Both routes must be /scroll ones. Entering scroll mode (a plain or
   * /from route redirected by `enterScrollMode`) is not a request to move:
   * the video is seeded via `startPosition` there, which resumes saved
   * reading progress.
   */
  positionRequested(previousOptions: any, options: any) {
    if (!previousOptions || !("scroll" in previousOptions)) {
      return;
    }
    if (!("scroll" in options)) {
      return;
    }
    // Plays from the requested position (matching the "goto" click mode), so
    // the scroll-follow brings that part of the text into view.
    this.controller?.gotoWord(Number(options.scroll) || 0);
  }

  remove() {
    this.controller?.remove();
    this.controller = undefined;
    this.controlsState = undefined;
    this.deps.setControlsState(undefined);
  }

  removeYouTube() {
    this.controller?.removeYouTube();
  }

  saveYouTubeID(userInput: string) {
    this.controller?.saveYouTubeID(userInput);
  }

  async submitYouTubeTranscript(
    transcript: string,
    mode: ApplyTranscriptMode
  ): Promise<{ textReplaced: boolean }> {
    return (
      (await this.controller?.submitYouTubeTranscript(transcript, mode)) ?? {
        textReplaced: false,
      }
    );
  }

  private getStartPosition() {
    const routePosition = routeStartPosition(this.deps.options);
    if (routePosition !== undefined) {
      return routePosition;
    }
    if (stores.bookModel.book.value.totalWords < 650) {
      return 0;
    }
    const userBookPosition = stores.bookModel.book.value.userBook?.position;
    return userBookPosition ? Math.max(0, Number(userBookPosition) - 10) : 0;
  }

  private enterScrollMode = () => {
    const { appRouter, options } = this.deps;
    if ("scroll" in options) {
      return;
    }

    const position = options.scroll || options.from || options.to || 0;
    appRouter.navigate("library/" + options.bookID + "/scroll/" + position, {
      trigger: true,
      replace: true,
    });
  };

  private showControls = () => {
    if (!this.controlsState) {
      return;
    }
    this.controlsState = {
      ...this.controlsState,
      showControlsKey: ++this.showControlsKey,
    };
    this.deps.setControlsState(this.controlsState);
  };

  private setMediaPanelHeight = (height: number) => {
    this.deps.setMediaPanelHeight(height, this.deps.updateLayout);
  };

  // TODO: review before enabling again - VIEWPORT_CSS
  private hideFooter = () => {
    this.deps.updateFooterState({ hidden: true, marginButtonsHidden: true });
    this.deps.setPageTextViewportNoFooter(true);
    this.deps.updateLayout();
  };

  private showFooter = () => {
    this.deps.updateFooterState({ hidden: false, marginButtonsHidden: false });
    this.deps.setPageTextViewportNoFooter(false);
    this.deps.updateLayout();
  };

  private setSyncAudioPosition = (position: number) => {
    const userBook = stores.bookModel.book.value.userBook;
    const logEveryXWords = 30;
    const lastPosition = userBook
      ? Number(userBook.position ?? 0)
      : this.lastPositionSession ?? 0;

    if (Math.abs(lastPosition - position) < logEveryXWords) {
      return;
    }

    if (userBook) {
      this.deps.updateUserBookPosition(position);
    } else {
      this.lastPositionSession = position;
    }

    this.deps.onReadingPosition(position);
  };
}
